import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity, AlertTriangle, BarChart3, Eye, Gift, Image as ImageIcon,
  Loader2, Palette, RefreshCw, RotateCw, ShieldAlert, Ticket,
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { formatMoney } from './lib/formatters';
import {
  canRetryCredit, CREDIT_STATUS_LABELS, CREDIT_STATUS_TONE, errorText,
  ignoredFields, partnerApi,
} from './lib/flexrixPartner';
import {
  Busy, Empty, Field, IgnoredFields, JsonEditor, Panel, Problem,
} from './components/SportDigiBits';

/**
 * Flexrix (BetConstruct) Sportsbook — self-servis yönetim ekranı.
 *
 * Sağlayıcının **iki yüzeyi** vardır: para akışı (cüzdan callback'i, bahis,
 * cashout) ve yönetim. Birincisi kendiliğinden çalışır ve panelde yalnızca
 * ayar kartı olarak görünür (Oyun Kontrol → Spor & Cashout); bu ekran
 * ikincisidir — sağlayıcının kendi hub panelinde yapılan her şey buradan da
 * yapılabilir (EveryMatrix'teki EmSportsManager ve Digitain'deki
 * SportDigiManager'ın aynısı).
 *
 * Ekranın var olma sebebi tek bir cümledir: **ödemesi ölü kalan kupon.**
 * Sonuç ödemesi bizim cüzdanımıza ulaşamazsa sağlayıcı on sekiz kez dener,
 * sonra `dead` işaretler ve kendiliğinden bir daha denemez. Kalan sekmeler
 * (risk, görünürlük, tema, trading, marka, hesap, kombolar, banner, vitrin)
 * aynı yüzeyin öteki parçalarıdır ve hepsi sağlayıcının kaydına yazar: bizim
 * veritabanımızda karşılıkları **yoktur**, her sekme açıldığında okunur.
 */

const TABS = [
  { id: 'summary', label: 'Özet', icon: BarChart3 },
  { id: 'bets', label: 'Kuponlar', icon: Ticket },
  { id: 'risk', label: 'Risk & Limitler', icon: ShieldAlert },
  { id: 'visibility', label: 'Görünürlük & Ayarlar', icon: Eye },
  { id: 'appearance', label: 'Görünüm', icon: Palette },
  { id: 'trading', label: 'Trading & Markalar', icon: Activity },
  { id: 'content', label: 'Hesap & Kombolar', icon: Gift },
  { id: 'banners', label: 'Banner & Vitrin', icon: ImageIcon },
];

const CREDIT_FILTERS = [
  { value: '', label: 'Tümü' },
  { value: 'dead', label: 'Ölü ödeme' },
  { value: 'failed', label: 'Başarısız' },
  { value: 'pending', label: 'Kuyrukta' },
  { value: 'done', label: 'Ödendi' },
  { value: 'none', label: 'Ödeme yok' },
];

const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const Stat = ({ label, value, hint, tone = 'text-white' }) => (
  <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className={`mt-1 text-lg font-bold ${tone}`}>{value}</div>
    {hint && <div className="mt-1 text-[10px] text-slate-500">{hint}</div>}
  </div>
);

// --------------------------------------------------------------------------- #
// Özet
// --------------------------------------------------------------------------- #
const SummaryTab = () => {
  const [state, setState] = useState({ loading: true });

  const load = useCallback(async () => {
    setState({ loading: true });
    try {
      const [summary, alerts, flags, wallet] = await Promise.all([
        partnerApi.summary().catch(() => ({ data: {} })),
        partnerApi.alerts({ limit: 50 }).catch(() => ({ data: {} })),
        partnerApi.flags({ limit: 50 }).catch(() => ({ data: {} })),
        partnerApi.wallet().catch(() => ({ data: {} })),
      ]);
      setState({
        loading: false,
        summary: summary.data?.summary || summary.data || {},
        alerts: alerts.data?.alerts || alerts.data?.items || [],
        flags: flags.data?.flags || flags.data?.items || [],
        wallet: wallet.data?.wallet || {},
      });
    } catch (error) {
      setState({ loading: false, error: errorText(error) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (state.loading) return <Busy />;
  if (state.error) return <Problem>{state.error}</Problem>;

  const summary = state.summary || {};
  return (
    <div className="space-y-4">
      <Panel
        title="Sağlayıcıdaki özet"
        description={
          'Rakamlar sağlayıcının kendi defterinden gelir ve bizim '
          + 'raporlarımızdan ayrıdır. Ödeme yalnızca kazanan kuponları sayar; '
          + 'komisyon bunun üzerinden Sportsbook cüzdanınızdan kesilir.'
        }
        actions={(
          <button
            type="button"
            className="admin-btn-secondary inline-flex items-center gap-2 !py-1 !text-[11px]"
            onClick={load}
          >
            <RefreshCw size={12} /> Yenile
          </button>
        )}
      >
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Kupon" value={number(summary.bets).toLocaleString('tr-TR')} hint={`Açık: ${number(summary.open_bets)}`} />
          <Stat label="Bahis" value={formatMoney(number(summary.stake))} />
          <Stat label="Ödeme" value={formatMoney(number(summary.payout))} />
          <Stat
            label="Komisyon cüzdanı"
            value={formatMoney(number(state.wallet.balance ?? state.wallet.amount))}
            tone="text-amber-300"
            hint="Boşalırsa sağlayıcı bahsi kabul etmez"
          />
        </div>
      </Panel>

      <Panel
        title="Uyarılar"
        description={
          'Sağlayıcının kendi uyarı defteri. Ödeme ölü kaldığında '
          + '(credit_dead) ve yanıtı alınamayan bir bahis geri alındığında '
          + '(debit_unknown) burada satır oluşur — ödeme olanlar Kuponlar '
          + 'sekmesinden çözülür.'
        }
      >
        {state.alerts?.length ? (
          <ul className="space-y-1">
            {state.alerts.slice(0, 30).map((row, index) => (
              <li
                key={row.id || index}
                className="flex flex-wrap items-center gap-2 rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px]"
              >
                <AlertTriangle
                  size={12}
                  className={row.category === 'credit_dead'
                    ? 'text-rose-400' : 'text-amber-300'}
                />
                <span className="text-slate-200">
                  {row.text || row.category || 'Uyarı'}
                </span>
                {row.bet_id && (
                  <span className="text-[10px] text-slate-500">
                    kupon #{row.bet_id}
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <Empty>Uyarı yok.</Empty>
        )}
      </Panel>

      <Panel
        title="İşaretlenen oyuncu ve maçlar"
        description={
          'İnceleme işareti bahsi **engellemez**: sağlayıcı yalnızca dikkat '
          + 'çeker. Eşikler Risk & Limitler sekmesindeki userFlags / '
          + 'matchFlags alanlarındadır.'
        }
      >
        {state.flags?.length ? (
          <ul className="space-y-1">
            {state.flags.slice(0, 30).map((row, index) => (
              <li
                key={row.id || index}
                className="rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px] text-slate-300"
              >
                {row.text || row.reason || JSON.stringify(row).slice(0, 160)}
              </li>
            ))}
          </ul>
        ) : (
          <Empty>İşaretlenen kayıt yok.</Empty>
        )}
      </Panel>
    </div>
  );
};

// --------------------------------------------------------------------------- #
// Kuponlar
// --------------------------------------------------------------------------- #
const BetsTab = () => {
  const { toast } = useToast();
  const [creditStatus, setCreditStatus] = useState('dead');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setProblem('');
    try {
      const params = { limit: 100 };
      if (creditStatus) params.credit_status = creditStatus;
      const { data } = await partnerApi.bets(params);
      setRows(data?.bets || []);
      setTotal(number(data?.total));
    } catch (error) {
      setProblem(errorText(error, 'Kupon listesi alınamadı'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [creditStatus]);

  useEffect(() => { load(); }, [load]);

  const retry = async (row) => {
    const id = String(row.id ?? row.bet_id ?? '');
    setBusy(id);
    try {
      await partnerApi.retryCredit(id);
      toast({
        title: 'Ödeme yeniden kuyruğa alındı',
        description:
          `Kupon #${id}. Ödeme ilk denemedeki kimlikle çıkar; bizde zaten `
          + 'işlenmişse tekrar sayılmaz.',
      });
      await load();
    } catch (error) {
      toast({
        title: 'Ödeme kuyruğa alınamadı',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="space-y-4">
      <Panel
        title="Sağlayıcıdaki kuponlar"
        description={
          'Bu liste sağlayıcının defteridir ve bizim Bahis Geçmişi '
          + 'ekranımızdan ayrıdır: oradaki satırlar para hareketinden doğar, '
          + 'buradaki satır ödemenin sağlayıcı tarafındaki durumunu taşır. '
          + 'İkisi yalnızca bir durumda ayrışır ve o durum buradan çözülür: '
          + 'ödemesi başarısız ya da ölü kalan kupon.'
        }
        actions={(
          <button
            type="button"
            className="admin-btn-secondary inline-flex items-center gap-2 !py-1 !text-[11px]"
            onClick={load}
          >
            <RefreshCw size={12} /> Yenile
          </button>
        )}
      >
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <Field label="Ödeme durumu">
            <select
              className="admin-input admin-input-w-sm"
              value={creditStatus}
              onChange={(event) => setCreditStatus(event.target.value)}
            >
              {CREDIT_FILTERS.map((row) => (
                <option key={row.value} value={row.value}>{row.label}</option>
              ))}
            </select>
          </Field>
        </div>

        <Problem>{problem}</Problem>

        {loading ? <Busy /> : null}

        {!loading && !rows.length && (
          <Empty>
            {creditStatus === 'dead'
              ? 'Ölü ödeme yok — sağlayıcının bütün ödemeleri bize ulaşmış.'
              : 'Bu süzgeçle kupon bulunamadı.'}
          </Empty>
        )}

        {!loading && rows.length > 0 && (
          <>
            <p className="mb-2 text-[11px] text-slate-500">
              {total.toLocaleString('tr-TR')} kayıttan {rows.length} tanesi.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-1">Kupon</th>
                    <th>Bahis</th>
                    <th>Ödeme</th>
                    <th>Durum</th>
                    <th>Ödeme durumu</th>
                    <th>Deneme</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const id = String(row.id ?? row.bet_id ?? '');
                    const credit = String(row.credit_status || 'none');
                    return (
                      <tr key={id} className="border-t border-slate-800">
                        <td className="py-1 font-semibold text-white">#{id}</td>
                        <td>{formatMoney(number(row.amount ?? row.stake))}</td>
                        <td>{formatMoney(number(row.payout))}</td>
                        <td className="text-slate-400">{row.status || row.outcome || '—'}</td>
                        <td className={CREDIT_STATUS_TONE[credit] || ''}>
                          {CREDIT_STATUS_LABELS[credit] || credit}
                        </td>
                        <td className="text-slate-500">
                          {row.credit_attempts ?? 0}
                          {row.credit_last_error ? ` · ${row.credit_last_error}` : ''}
                        </td>
                        <td className="text-right">
                          {canRetryCredit(credit) && (
                            <button
                              type="button"
                              className="admin-btn-primary inline-flex items-center gap-1 !py-1 !text-[10px]"
                              disabled={busy === id}
                              onClick={() => retry(row)}
                            >
                              {busy === id
                                ? <Loader2 size={11} className="animate-spin" />
                                : <RotateCw size={11} />}
                              Yeniden kuyruğa al
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Panel>
    </div>
  );
};

// --------------------------------------------------------------------------- #
// Risk & limitler
// --------------------------------------------------------------------------- #
const RiskTab = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState('');
  const [risk, setRisk] = useState({});
  const [ignored, setIgnored] = useState([]);
  const [rules, setRules] = useState([]);
  const [audit, setAudit] = useState(null);
  const [ruleDraft, setRuleDraft] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setProblem('');
    try {
      const [config, ruleRes, auditRes] = await Promise.all([
        partnerApi.riskConfig(),
        partnerApi.riskRules().catch(() => ({ data: {} })),
        partnerApi.riskAudit().catch(() => ({ data: null })),
      ]);
      setRisk(config.data?.risk || {});
      const list = ruleRes.data?.rules || ruleRes.data?.items || [];
      setRules(Array.isArray(list) ? list : []);
      setAudit(auditRes.data || null);
    } catch (error) {
      setProblem(errorText(error, 'Risk yapılandırması alınamadı'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy('config');
    try {
      const { data } = await partnerApi.saveRiskConfig({ risk });
      setIgnored(ignoredFields(data));
      toast({ title: 'Risk yapılandırması kaydedildi' });
      await load();
    } catch (error) {
      toast({
        title: 'Kaydedilemedi',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  const createRule = async () => {
    let body;
    try {
      body = JSON.parse(ruleDraft);
    } catch {
      toast({ title: 'Geçersiz kural JSON\'u', variant: 'destructive' });
      return;
    }
    setBusy('rule');
    try {
      await partnerApi.saveRiskRule(body);
      toast({ title: 'Kural kaydedildi' });
      setRuleDraft('');
      await load();
    } catch (error) {
      toast({
        title: 'Kural kaydedilemedi',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  if (loading) return <Busy />;

  return (
    <div className="space-y-4">
      <Problem>{problem}</Problem>

      <Panel
        title="Risk yapılandırması"
        description={
          'Katmanlı risk limitleri (genel, tekli, canlı, ülke başına, '
          + 'oyuncu/maç bayrakları). Sağlayıcı bu ucu **bütün olarak** '
          + 'değiştirir — önce okunur, değiştirilir ve eksiksiz geri '
          + 'gönderilir. Tanınmayan bir alan 422 ile reddedilir.'
        }
      >
        <JsonEditor value={risk} onChange={setRisk} rows={16} />
        <button
          type="button"
          className="admin-btn-primary mt-2 inline-flex items-center gap-2 !py-1 !text-[11px]"
          disabled={busy === 'config'}
          onClick={save}
        >
          {busy === 'config' ? <Loader2 size={12} className="animate-spin" /> : <ShieldAlert size={12} />}
          Risk yapılandırmasını kaydet
        </button>
        <IgnoredFields fields={ignored} />
      </Panel>

      <Panel
        title="Kapsamlı risk kuralları"
        description={
          'Merchant/oyuncu/ülke/spor/lig/maç/market kapsamında kurallar. '
          + '``(scope, scope_id)`` anahtarıdır; aynı anahtara yazmak tam '
          + 'değiştirir (gönderilmeyen alanlar null olur).'
        }
      >
        {!rules.length ? (
          <Empty>Kural yok.</Empty>
        ) : (
          <ul className="space-y-1">
            {rules.slice(0, 100).map((row, index) => (
              <li
                key={row.id || index}
                className="flex flex-wrap items-center gap-2 rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px] text-slate-300"
              >
                <span className="font-semibold text-white">#{row.id || index}</span>
                {row.scope && <span>{row.scope}</span>}
                {row.scope_id && <span className="text-slate-500">{row.scope_id}</span>}
                {row.blocked && <span className="text-rose-300">engelli</span>}
                {row.suspicious && <span className="text-amber-300">şüpheli</span>}
                <button
                  type="button"
                  className="admin-btn-secondary ml-auto !py-1 !text-[10px]"
                  onClick={async () => {
                    try {
                      await partnerApi.deleteRiskRule(String(row.id ?? row.rule_id ?? ''));
                      toast({ title: 'Kural silindi' });
                      await load();
                    } catch (error) {
                      toast({
                        title: 'Silinemedi',
                        description: errorText(error),
                        variant: 'destructive',
                      });
                    }
                  }}
                >
                  Sil
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 border-t border-slate-800 pt-3">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Yeni kural
          </div>
          <JsonEditor
            value={{}}
            onChange={(value) => setRuleDraft(JSON.stringify(value))}
            rows={6}
            hint='Örnek: {"rule":{"scope":"player","scope_id":"<oyuncu>","blocked":true,"note":"fraud"}}'
          />
          <button
            type="button"
            className="admin-btn-primary mt-2 inline-flex items-center gap-2 !py-1 !text-[11px]"
            disabled={busy === 'rule'}
            onClick={createRule}
          >
            Kuralı ekle
          </button>
        </div>
      </Panel>

      {audit && (
        <Panel title="Risk karar izi" description="Risk kararlarının salt okunur kaydı.">
          <JsonEditor value={audit} onChange={() => {}} rows={8} hint="Salt okunur — düzenleme kaydedilmez." />
        </Panel>
      )}
    </div>
  );
};

// --------------------------------------------------------------------------- #
// Görünürlük & ayarlar
// --------------------------------------------------------------------------- #
const VisibilityTab = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const [visibility, setVisibility] = useState({});
  const [settings, setSettings] = useState({});
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setProblem('');
    try {
      const [visRes, setRes] = await Promise.all([
        partnerApi.visibility().catch(() => ({ data: {} })),
        partnerApi.settings().catch(() => ({ data: {} })),
      ]);
      setVisibility(visRes.data?.visibility || {});
      setSettings(setRes.data?.settings || setRes.data || {});
    } catch (error) {
      setProblem(errorText(error, 'Görünürlük alınamadı'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (kind) => {
    setBusy(kind);
    try {
      if (kind === 'visibility') await partnerApi.saveVisibility(visibility);
      if (kind === 'settings') await partnerApi.saveSettings(settings);
      toast({ title: 'Kaydedildi' });
      await load();
    } catch (error) {
      toast({
        title: 'Kaydedilemedi',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  if (loading) return <Busy />;

  return (
    <div className="space-y-4">
      <Problem>{problem}</Problem>

      <Panel
        title="Görünürlük"
        description={
          'Gizlenen spor/ülke/lig/maç/market kümesi. Gövde anahtar başına '
          + 'birleştirilir; ``{off:[…]}`` listeleri bütün olarak değiştirilir.'
        }
      >
        <JsonEditor
          value={visibility}
          onChange={setVisibility}
          rows={12}
          hint='Örnek: {"sports":{"off":[3]},"tournaments":{"off":[4520]}}'
        />
        <button
          type="button"
          className="admin-btn-primary mt-2 inline-flex items-center gap-2 !py-1 !text-[11px]"
          disabled={busy === 'visibility'}
          onClick={() => save('visibility')}
        >
          Görünürlüğü kaydet
        </button>
      </Panel>

      <Panel
        title="Ayarlar"
        description={
          'Bahis türleri, diller, para birimleri, bildirim ve cashout '
          + 'anahtarları. Gövde **birleştirilir**: yalnızca gönderilen üst '
          + 'düzey anahtarlar değişir.'
        }
      >
        <JsonEditor
          value={settings}
          onChange={setSettings}
          rows={10}
          hint='Örnek: {"default_language":"tr","languages":["tr","en"],"cashout":{"enabled":true}}'
        />
        <button
          type="button"
          className="admin-btn-primary mt-2 inline-flex items-center gap-2 !py-1 !text-[11px]"
          disabled={busy === 'settings'}
          onClick={() => save('settings')}
        >
          Ayarları kaydet
        </button>
      </Panel>
    </div>
  );
};

// --------------------------------------------------------------------------- #
// Görünüm
// --------------------------------------------------------------------------- #
const AppearanceTab = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const [theme, setTheme] = useState({});
  const [presets, setPresets] = useState([]);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setProblem('');
    try {
      const [themeRes, presetRes] = await Promise.all([
        partnerApi.theme().catch(() => ({ data: {} })),
        partnerApi.themePresets().catch(() => ({ data: {} })),
      ]);
      setTheme(themeRes.data?.theme || themeRes.data || {});
      const rows = presetRes.data?.presets || presetRes.data?.items || [];
      setPresets(Array.isArray(rows) ? rows : []);
    } catch (error) {
      setProblem(errorText(error, 'Tema alınamadı'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy('theme');
    try {
      await partnerApi.saveTheme({ theme });
      toast({ title: 'Tema kaydedildi' });
      await load();
    } catch (error) {
      toast({
        title: 'Kaydedilemedi',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  if (loading) return <Busy />;

  return (
    <div className="space-y-4">
      <Problem>{problem}</Problem>

      <Panel
        title="Hazır şemalar"
        description="Sağlayıcının hazır tema listesi (renk jetonu + :root CSS başlangıcı)."
      >
        {!presets.length ? (
          <Empty>Şema listesi boş.</Empty>
        ) : (
          <ul className="flex flex-wrap gap-1">
            {presets.slice(0, 40).map((row) => (
              <li
                key={row.id || row}
                className="rounded border border-slate-800 bg-slate-950/40 px-2 py-1 text-[11px] text-slate-300"
              >
                {row.id || row.title || row}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel
        title="Tema"
        description={
          'Özel tema: 8 renk jetonu (``scheme``), serbest CSS (``css``), logo '
          + 've ad. Hub içindeki hazır tema seçicisinden farklıdır. Sağlayıcı '
          + 'tanımadığı alanı 422 ile reddeder.'
        }
      >
        <JsonEditor value={theme} onChange={setTheme} rows={12} />
        <button
          type="button"
          className="admin-btn-primary mt-2 inline-flex items-center gap-2 !py-1 !text-[11px]"
          disabled={busy === 'theme'}
          onClick={save}
        >
          Temayı kaydet
        </button>
      </Panel>
    </div>
  );
};

// --------------------------------------------------------------------------- #
// Trading & markalar
// --------------------------------------------------------------------------- #
const TradingTab = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const [live, setLive] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [brands, setBrands] = useState([]);
  const [draft, setDraft] = useState({
    scope: 'fixture', fixture_id: '', market_id: '', outcome_id: '',
    league_id: '', action: 'margin', margin_pct: '',
  });
  const [bulkFixture, setBulkFixture] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setProblem('');
    try {
      const [liveRes, overRes, brandsRes] = await Promise.all([
        partnerApi.tradingLive().catch(() => ({ data: {} })),
        partnerApi.overrides().catch(() => ({ data: {} })),
        partnerApi.brands().catch(() => ({ data: {} })),
      ]);
      setLive(liveRes.data?.games || liveRes.data?.items || []);
      setOverrides(overRes.data?.overrides || overRes.data?.items || []);
      const rows = brandsRes.data?.brands || brandsRes.data?.items || [];
      setBrands(Array.isArray(rows) ? rows : []);
    } catch (error) {
      setProblem(errorText(error, 'Trading verisi alınamadı'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createOverride = async () => {
    const scopeId = draft[`${draft.scope}_id`];
    if (!scopeId) {
      toast({ title: 'Kapsam kimliği gerekli', variant: 'destructive' });
      return;
    }
    setBusy('override');
    try {
      const body = { scope: draft.scope, action: draft.action };
      body[`${draft.scope}_id`] = scopeId;
      if (draft.action === 'margin') body.margin_pct = Number(draft.margin_pct);
      await partnerApi.saveOverride(body);
      toast({ title: 'Oran müdahalesi eklendi' });
      await load();
    } catch (error) {
      toast({
        title: 'Eklenemedi',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  if (loading) return <Busy />;

  return (
    <div className="space-y-4">
      <Problem>{problem}</Problem>

      <Panel title="Canlı maçlar" description="Trading'e konu olabilecek canlı maçlar (dakika, skor).">
        {!live.length ? (
          <Empty>Canlı maç yok.</Empty>
        ) : (
          <ul className="space-y-1">
            {live.slice(0, 30).map((row) => (
              <li
                key={row.fixture_id || row.id}
                className="rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px] text-slate-300"
              >
                <span className="font-semibold text-white">#{row.fixture_id}</span>{' '}
                {row.home} - {row.away} · {row.home_score}-{row.away_score} ·{' '}
                {row.clock || '—'}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel
        title="Oran müdahaleleri"
        description={
          'Oran kuralı hem vitrini hem kabul edilen bahsi etkiler. Yayılma bir '
          + 'önbellek turu kadar gecikir (≤ 20 sn).'
        }
      >
        {!overrides.length ? (
          <Empty>Kural yok.</Empty>
        ) : (
          <ul className="space-y-1">
            {overrides.slice(0, 100).map((row, index) => (
              <li
                key={row.id || index}
                className="flex flex-wrap items-center gap-2 rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px] text-slate-300"
              >
                <span className="font-semibold text-white">#{row.id || index}</span>
                <span>{row.scope}</span>
                <span className="text-slate-500">{row.scope_id || row.fixture_id || '—'}</span>
                <span>{row.action}</span>
                {row.margin_pct != null && <span>%{row.margin_pct}</span>}
                {row.fixed_odds != null && <span>@ {row.fixed_odds}</span>}
                <button
                  type="button"
                  className="admin-btn-secondary ml-auto !py-1 !text-[10px]"
                  onClick={async () => {
                    try {
                      await partnerApi.deleteOverride(String(row.id ?? row.rule_id ?? ''));
                      toast({ title: 'Kural silindi' });
                      await load();
                    } catch (error) {
                      toast({
                        title: 'Silinemedi',
                        description: errorText(error),
                        variant: 'destructive',
                      });
                    }
                  }}
                >
                  Sil
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 border-t border-slate-800 pt-3">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Yeni oran müdahalesi
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Kapsam">
              <select
                className="admin-input"
                value={draft.scope}
                onChange={(event) => setDraft({ ...draft, scope: event.target.value })}
              >
                <option value="outcome">Outcome</option>
                <option value="market">Market</option>
                <option value="fixture">Maç</option>
                <option value="league">Lig</option>
                <option value="merchant">Merchant</option>
              </select>
            </Field>
            <Field label={`${draft.scope}_id`}>
              <input
                className="admin-input"
                value={draft[`${draft.scope}_id`] || ''}
                onChange={(event) => setDraft({ ...draft, [`${draft.scope}_id`]: event.target.value })}
              />
            </Field>
            <Field label="Eylem">
              <select
                className="admin-input"
                value={draft.action}
                onChange={(event) => setDraft({ ...draft, action: event.target.value })}
              >
                <option value="margin">Marj</option>
                <option value="suspend">Askıya al</option>
                <option value="hide">Gizle</option>
                <option value="fixed">Sabit oran</option>
              </select>
            </Field>
            {draft.action === 'margin' && (
              <Field label="margin_pct (-90…90)">
                <input
                  className="admin-input"
                  value={draft.margin_pct}
                  onChange={(event) => setDraft({ ...draft, margin_pct: event.target.value })}
                  placeholder="10"
                />
              </Field>
            )}
          </div>
          <button
            type="button"
            className="admin-btn-primary mt-3 inline-flex items-center gap-2 !py-1 !text-[11px]"
            disabled={busy === 'override'}
            onClick={createOverride}
          >
            Kuralı ekle
          </button>
        </div>
      </Panel>

      <Panel
        title="Markalar (alt bayi)"
        description={
          'Tek entegrasyonda birden çok site. Sizin ayarlarınız varsayılandır; '
          + 'marka yalnızca gönderdiği eksenleri geçersiz kılar. Bir marka '
          + '``launch`` adresine ``&brand=<id>`` ile seçilir.'
        }
      >
        {!brands.length ? (
          <Empty>Marka yok.</Empty>
        ) : (
          <ul className="space-y-1">
            {brands.slice(0, 50).map((row) => (
              <li
                key={row.id || row.brand_id}
                className="flex flex-wrap items-center gap-2 rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px] text-slate-300"
              >
                <span className="font-semibold text-white">
                  {row.name || row.brand_id || row.id}
                </span>
                {row.brand_id && <span className="text-slate-500">#{row.brand_id}</span>}
                <button
                  type="button"
                  className="admin-btn-secondary ml-auto !py-1 !text-[10px]"
                  onClick={async () => {
                    try {
                      await partnerApi.deleteBrand(String(row.brand_id ?? row.id ?? ''));
                      toast({ title: 'Marka silindi' });
                      await load();
                    } catch (error) {
                      toast({
                        title: 'Silinemedi',
                        description: errorText(error),
                        variant: 'destructive',
                      });
                    }
                  }}
                >
                  Sil
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
};

// --------------------------------------------------------------------------- #
// Hesap & kombolar
// --------------------------------------------------------------------------- #
const ContentTab = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const [account, setAccount] = useState({});
  const [combos, setCombos] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [testPlayers, setTestPlayers] = useState({});
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setProblem('');
    try {
      const [accRes, combosRes, couponsRes, testRes] = await Promise.all([
        partnerApi.account().catch(() => ({ data: {} })),
        partnerApi.dailyCombos().catch(() => ({ data: {} })),
        partnerApi.coupons().catch(() => ({ data: {} })),
        partnerApi.testPlayers().catch(() => ({ data: {} })),
      ]);
      setAccount(accRes.data?.account || accRes.data || {});
      setCombos(Array.isArray(combosRes.data?.items) ? combosRes.data.items : []);
      setCoupons(Array.isArray(couponsRes.data?.items) ? couponsRes.data.items : []);
      setTestPlayers(testRes.data || {});
    } catch (error) {
      setProblem(errorText(error, 'İçerik alınamadı'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveAccount = async () => {
    setBusy('account');
    try {
      await partnerApi.saveAccount({ account });
      toast({ title: 'Hesap panelleri kaydedildi' });
      await load();
    } catch (error) {
      toast({
        title: 'Kaydedilemedi',
        description: errorText(error),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  const remove = async (kind, id) => {
    setBusy(`${kind}-${id}`);
    try {
      if (kind === 'combo') await partnerApi.deleteDailyCombo(id);
      if (kind === 'coupon') await partnerApi.deleteCoupon(id);
      toast({ title: 'Silindi' });
      await load();
    } catch (error) {
      toast({ title: 'Silinemedi', description: errorText(error), variant: 'destructive' });
    } finally {
      setBusy('');
    }
  };

  if (loading) return <Busy />;

  return (
    <div className="space-y-4">
      <Problem>{problem}</Problem>

      <Panel
        title="Hesap panelleri"
        description={
          'Oyun içi hesap menüsündeki paneller (ödül, sadakat, promosyon). '
          + '``external_sections`` listelenen bölümler kendi sayfamıza '
          + '``open_section`` mesajı olarak düşer; ``hide_selectors`` menü '
          + 'satırlarını gizler.'
        }
      >
        <JsonEditor value={account} onChange={setAccount} rows={10} />
        <button
          type="button"
          className="admin-btn-primary mt-2 inline-flex items-center gap-2 !py-1 !text-[11px]"
          disabled={busy === 'account'}
          onClick={saveAccount}
        >
          Hesap panellerini kaydet
        </button>
      </Panel>

      <Panel title="Günün kombosu" description="Çoklu seçim kombosu; tam kazançta bonus çarpanı uygulanabilir.">
        {!combos.length ? (
          <Empty>Tanımlı kombo yok.</Empty>
        ) : (
          <ul className="space-y-1">
            {combos.slice(0, 50).map((row) => (
              <li
                key={row.id}
                className="flex flex-wrap items-center gap-2 rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px] text-slate-300"
              >
                <span className="font-semibold text-white">{row.title || `#${row.id}`}</span>
                <span className="text-slate-500">#{row.id}</span>
                {row.display_odds != null && <span>@ {row.display_odds}</span>}
                {row.bonus_pct != null && <span className="text-emerald-300">%{row.bonus_pct}</span>}
                <button
                  type="button"
                  className="admin-btn-secondary ml-auto !py-1 !text-[10px]"
                  disabled={busy === `combo-${row.id}`}
                  onClick={() => remove('combo', row.id)}
                >
                  Sil
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Hazır kuponlar" description="Tek tıkla oynanan hazır kupon şablonları.">
        {!coupons.length ? (
          <Empty>Tanımlı kupon yok.</Empty>
        ) : (
          <ul className="space-y-1">
            {coupons.slice(0, 50).map((row) => (
              <li
                key={row.id}
                className="flex flex-wrap items-center gap-2 rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px] text-slate-300"
              >
                <span className="font-semibold text-white">{row.title || `#${row.id}`}</span>
                <span className="text-slate-500">#{row.id}</span>
                {row.display_odds != null && <span>@ {row.display_odds}</span>}
                <button
                  type="button"
                  className="admin-btn-secondary ml-auto !py-1 !text-[10px]"
                  disabled={busy === `coupon-${row.id}`}
                  onClick={() => remove('coupon', row.id)}
                >
                  Sil
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel
        title="Test oyuncuları"
        description="``test_only`` kombolar/kuponlar yalnızca bu listedeki oyunculara görünür."
      >
        <JsonEditor value={testPlayers} onChange={setTestPlayers} rows={6} hint='Örnek: {"player_ids":["<oyuncu-id>"]}' />
        <button
          type="button"
          className="admin-btn-primary mt-2 inline-flex items-center gap-2 !py-1 !text-[11px]"
          disabled={busy === 'test'}
          onClick={async () => {
            setBusy('test');
            try {
              await partnerApi.saveTestPlayers(testPlayers);
              toast({ title: 'Test oyuncuları kaydedildi' });
              await load();
            } catch (error) {
              toast({ title: 'Kaydedilemedi', description: errorText(error), variant: 'destructive' });
            } finally {
              setBusy('');
            }
          }}
        >
          Kaydet
        </button>
      </Panel>
    </div>
  );
};

// --------------------------------------------------------------------------- #
// Banner & vitrin
// --------------------------------------------------------------------------- #
const BannersTab = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const [banners, setBanners] = useState([]);
  const [logos, setLogos] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [featuredMatchId, setFeaturedMatchId] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setProblem('');
    try {
      const [bannerRes, logoRes, featuredRes] = await Promise.all([
        partnerApi.banners().catch(() => ({ data: {} })),
        partnerApi.teamLogos({ limit: 100 }).catch(() => ({ data: {} })),
        partnerApi.featured({ include_stale: true }).catch(() => ({ data: {} })),
      ]);
      setBanners(Array.isArray(bannerRes.data?.items) ? bannerRes.data.items : []);
      setLogos(logoRes.data?.items || []);
      setFeatured(featuredRes.data?.items || []);
    } catch (error) {
      setProblem(errorText(error, 'Vitrin alınamadı'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (kind, id) => {
    setBusy(`${kind}-${id}`);
    try {
      if (kind === 'banner') await partnerApi.deleteBanner(id);
      if (kind === 'logo') await partnerApi.deleteTeamLogo(id);
      toast({ title: 'Silindi' });
      await load();
    } catch (error) {
      toast({ title: 'Silinemedi', description: errorText(error), variant: 'destructive' });
    } finally {
      setBusy('');
    }
  };

  const toggleFeatured = async () => {
    const id = featuredMatchId.trim();
    if (!id) {
      toast({ title: 'Maç kimliği gerekli', variant: 'destructive' });
      return;
    }
    setBusy('featured');
    try {
      const already = featured.some((row) => String(row.match_id) === id);
      await partnerApi.saveFeatured(id, { is_featured: !already });
      toast({ title: already ? 'Öne çıkarmadan kaldırıldı' : 'Öne çıkarıldı' });
      setFeaturedMatchId('');
      await load();
    } catch (error) {
      toast({ title: 'İşlem başarısız', description: errorText(error), variant: 'destructive' });
    } finally {
      setBusy('');
    }
  };

  if (loading) return <Busy />;

  return (
    <div className="space-y-4">
      <Problem>{problem}</Problem>

      <Panel
        title="Bannerlar"
        description={
          'Ana sayfa slider alanı. Görsel base64 (veri-ön eki olmadan) ile '
          + 'yüklenir; silmek geri dönüşü yoktur — önce pasife almak daha '
          + 'güvenlidir (programatik akışlarda kullanılır).'
        }
      >
        {!banners.length ? (
          <Empty>Banner yok.</Empty>
        ) : (
          <ul className="space-y-1">
            {banners.slice(0, 50).map((row) => (
              <li
                key={row.id}
                className="flex flex-wrap items-center gap-2 rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px] text-slate-300"
              >
                <span className="font-semibold text-white">{row.title || row.id}</span>
                <span className="text-slate-500">#{row.id}</span>
                {row.linkUrl && <span className="text-slate-500">{row.linkUrl}</span>}
                {row.isActive === false && <span className="text-amber-300">pasif</span>}
                <button
                  type="button"
                  className="admin-btn-secondary ml-auto !py-1 !text-[10px]"
                  disabled={busy === `banner-${row.id}`}
                  onClick={() => remove('banner', row.id)}
                >
                  Sil
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel
        title="Takım logoları"
        description={
          'Global takım logosu kaydı: bir merchant yükler, herkes kullanır. '
          + '``team_name`` normalize eşleşmeyle fixture cevaplarına eklenir.'
        }
      >
        {!logos.length ? (
          <Empty>Yüklenen logo yok.</Empty>
        ) : (
          <ul className="space-y-1">
            {logos.slice(0, 50).map((row) => (
              <li
                key={row.id}
                className="flex flex-wrap items-center gap-2 rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px] text-slate-300"
              >
                <span className="font-semibold text-white">{row.team_name}</span>
                <span className="text-slate-500">#{row.id}</span>
                <button
                  type="button"
                  className="admin-btn-secondary ml-auto !py-1 !text-[10px]"
                  disabled={busy === `logo-${row.id}`}
                  onClick={() => remove('logo', row.id)}
                >
                  Sil
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel
        title="Öne çıkan maçlar"
        description={
          'Ana sayfa vitrininde üstte duran maçlar. ``position`` küçük olan '
          + 'üsttedir; süresi geçen işaretler listede ayrıca görünür.'
        }
      >
        {!featured.length ? (
          <Empty>Öne çıkan maç yok.</Empty>
        ) : (
          <ul className="space-y-1">
            {featured.slice(0, 50).map((row) => (
              <li
                key={row.match_id}
                className="rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px] text-slate-300"
              >
                <span className="font-semibold text-white">#{row.match_id}</span>{' '}
                {row.home_team} - {row.away_team} · konum {row.position ?? '—'}
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 border-t border-slate-800 pt-3">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Öne çıkar / kaldır
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <Field label="Maç kimliği (match_id)">
              <input
                className="admin-input admin-input-w-md"
                value={featuredMatchId}
                onChange={(event) => setFeaturedMatchId(event.target.value)}
              />
            </Field>
            <button
              type="button"
              className="admin-btn-secondary !py-1 !text-[11px]"
              disabled={busy === 'featured'}
              onClick={toggleFeatured}
            >
              {featured.some((row) => String(row.match_id) === featuredMatchId.trim())
                ? 'Kaldır' : 'Öne çıkar'}
            </button>
          </div>
        </div>
      </Panel>
    </div>
  );
};

// --------------------------------------------------------------------------- #
// Kabuk
// --------------------------------------------------------------------------- #
const FlexrixManager = () => {
  const [tab, setTab] = useState('summary');

  const body = useMemo(() => {
    switch (tab) {
      case 'bets': return <BetsTab />;
      case 'risk': return <RiskTab />;
      case 'visibility': return <VisibilityTab />;
      case 'appearance': return <AppearanceTab />;
      case 'trading': return <TradingTab />;
      case 'content': return <ContentTab />;
      case 'banners': return <BannersTab />;
      default: return <SummaryTab />;
    }
  }, [tab]);

  return (
    <div className="space-y-4 p-4">
      <header>
        <h1 className="text-lg font-bold text-white">BetConstruct Yönetimi</h1>
        <p className="mt-1 max-w-4xl text-[12px] leading-relaxed text-slate-400">
          Sağlayıcının self-servis yönetim yüzeyi (Partner API + Hub API).
          Buradaki ayarların hiçbiri bizim veritabanımızda durmaz — hepsi
          doğrudan sağlayıcıya yazılır ve sayfa her açıldığında oradan okunur.
          Entegrasyonun kendi ayarları (merchant key, secret, callback onayı)
          <b className="text-slate-300"> Oyun Kontrol → Spor &amp; Cashout</b>
          {' '}altındadır. Ödemesi ölü kalan kupon Kuponlar sekmesinden
          yeniden kuyruğa alınır.
        </p>
      </header>

      <nav className="flex flex-wrap gap-1">
        {TABS.map((item) => {
          const Icon = item.icon;
          const active = tab === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-[11px] font-semibold transition ${
                active
                  ? 'bg-sky-500/15 text-sky-200'
                  : 'text-slate-400 hover:bg-slate-800/60 hover:text-slate-200'
              }`}
            >
              <Icon size={13} /> {item.label}
            </button>
          );
        })}
      </nav>

      {body}
    </div>
  );
};

export default FlexrixManager;
